import { useEffect, useRef, useState } from "react";
import { PageHeader } from "../../components/common/PageHeader";
import { StatCard } from "../../components/common/StatCard";
import { StatusBadge } from "../../components/common/StatusBadge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { PosService } from "../../services";
import { useAuth } from "../../store/AuthContext";
import { currency, dateTime } from "../../utils/format";
import { isSalesBlockedNow } from "../../utils/businessDay";
import { toast } from "sonner";
import { Wallet, PlayCircle, StopCircle } from "lucide-react";

const formatAmountInput = (value) => {
  if (value === "") return "";

  const cleaned = String(value).replace(/[^\d.]/g, "");
  if (cleaned === "") return "";

  const parts = cleaned.split(".");
  const whole = parts[0];
  const decimal = parts.length > 1 ? parts.slice(1).join("") : undefined;

  const formattedWhole = whole === "" ? "" : Number(whole).toLocaleString("en-US");

  return decimal !== undefined ? `${formattedWhole}.${decimal}` : formattedWhole;
};

const parseAmountInput = (value) => {
  const cleaned = String(value).replace(/[^\d.]/g, "");
  const parts = cleaned.split(".");

  if (parts.length <= 1) return cleaned;

  return `${parts[0]}.${parts.slice(1).join("")}`;
};

export const CashierSessionPage = () => {
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState("");
  const [closingActual, setClosingActual] = useState("");
  const [busy, setBusy] = useState(false);
  const [salesBlocked, setSalesBlocked] = useState(isSalesBlockedNow());

  const blockedWarningShownRef = useRef(false);

  const load = () => {
    setLoading(true);
    PosService.cashCurrent(user.userId)
      .then(setSession)
      .catch(() => setSession(null))
      .finally(() => setLoading(false));
  };


  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const checkBlocked = () => setSalesBlocked(isSalesBlockedNow());

    checkBlocked();

    const interval = setInterval(checkBlocked, 30000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (session && salesBlocked && !blockedWarningShownRef.current) {
      toast.warning("Sales are closed for the business day. Please close your cash session.");
      blockedWarningShownRef.current = true;
    }

    if (!session || !salesBlocked) {
      blockedWarningShownRef.current = false;
    }
  }, [session, salesBlocked]);

  const open = async () => {
    if (opening === "" || Number(opening) < 0) return toast.error("Opening cash must be ≥ 0");

    setBusy(true);
    try {
      await PosService.cashOpen({
        cashierId: user.userId,
        cashierName: user.fullName,
        openingCashAmount: Number(opening),
      });

      toast.success("Cash session opened");
      setOpening("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const close = async () => {

    if (closingActual === "" || Number(closingActual) < 0) {
      return toast.error("Enter actual closing cash");
    }

    setBusy(true);

    try {
      const closed = await PosService.cashClose({
        cashierId: user.userId,
        actualClosingCash: Number(closingActual),
      });

      toast.success(
        `Closed. Difference: ${currency(closed.cashDifference)}`
      );

      setClosingActual("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const expectedClosingCash = Number(session?.expectedClosingCash || 0);
  const closingDifference = closingActual === "" ? 0 : Number(closingActual) - expectedClosingCash;

  return (
    <div>
      <PageHeader title="Cash Session" subtitle="Open with your starting cash, close at end of shift." />

      {salesBlocked && session && (
        <div className="bms-card p-4 mb-5 border border-amber-200 bg-amber-50 text-amber-800">
          <div className="font-semibold">Sales are closed for the business day.</div>
          <div className="text-sm mt-1">
            Please close your open cash session before leaving.
          </div>
        </div>
      )}

      {loading ? <div className="text-slate-500">Loading…</div> : !session ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bms-card p-6">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-xl bg-gold-500 text-navy-900 grid place-items-center">
                <Wallet className="h-6 w-6" />
              </div>
              <div>
                <h3 className="font-display font-semibold text-slate-900 text-lg">Start your day</h3>
                <p className="text-sm text-slate-500">Enter the cash currently in the drawer.</p>
              </div>
            </div>

            <div className="mt-5">
              <Label className="text-xs text-slate-600">Opening cash (Rs.)</Label>
              <Input
                type="text"
                inputMode="decimal"
                value={formatAmountInput(opening)}
                onChange={(e) => setOpening(parseAmountInput(e.target.value))}
                className="h-12 text-lg"
                data-testid="opening-cash"
              />

              <Button
                onClick={open}
                disabled={busy}
                className="mt-4 w-full bg-navy-800 hover:bg-navy-900 text-white h-12"
              >
                <PlayCircle className="h-4 w-4 mr-2" /> Open Session
              </Button>
            </div>
          </div>

          <div className="bms-card p-6 text-sm text-slate-500">
            <p>You must open a session before creating sales. The system tracks cash sales after the opening time and calculates expected closing cash automatically.</p>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Opening cash" value={currency(session.openingCashAmount)} icon="Banknote" tone="navy" />
            <StatCard label="Cash sales" value={currency(session.cashSalesAmount)} icon="ShoppingCart" tone="emerald" />
            <StatCard label="Expected closing" value={currency(session.expectedClosingCash)} icon="BadgeDollarSign" tone="gold" />
            <StatCard label="Status" value={session.status} icon="Activity" tone="sky" hint={`Opened ${dateTime(session.openedAt)}`} />
          </div>

          <div className="bms-card p-6 mt-6 max-w-xl">
            <div className="flex items-center gap-2">
              <StatusBadge value="OPEN" />
            </div>

            <h3 className="font-display font-semibold text-slate-900 text-lg mt-3">Close session</h3>
            <p className="text-sm text-slate-500">Count the cash in the drawer and enter the actual amount.</p>

            <div className="mt-4">
              <Label className="text-xs text-slate-600">Actual closing cash (Rs.)</Label>
              <Input
                type="text"
                inputMode="decimal"
                value={formatAmountInput(closingActual)}
                onChange={(e) => setClosingActual(parseAmountInput(e.target.value))}
                className="h-12 text-lg"
                data-testid="closing-cash"
              />

              <Button
                onClick={close}
                disabled={busy}
                className="mt-4 w-full bg-rose-600 hover:bg-rose-700 text-white h-12"
              >
                <StopCircle className="h-4 w-4 mr-2" /> Close Session
              </Button>

              {closingActual !== "" && (
                <p className="text-xs mt-2 text-slate-500">
                  Difference will be:{" "}
                  <span className={closingDifference < 0 ? "text-rose-600" : "text-emerald-600"}>
                    {currency(closingDifference)}
                  </span>
                </p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const Row = ({ label, value, bold }) => (
  <div className="flex items-center justify-between">
    <span className="text-slate-500">{label}</span>
    <span className={bold ? "font-display font-semibold text-slate-900" : "text-slate-800"}>{value}</span>
  </div>
);